import { useCallback, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Separator } from "@/components/ui/separator";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/layout/app-sidebar";
import { PageBreadcrumb } from "@/components/layout/page-breadcrumb";
import { PageCrumbsProvider } from "@/components/layout/page-crumbs";
import { LanguageToggle } from "@/components/layout/language-toggle";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { NotificationsBell } from "@/features/pipeline/notifications-bell";
import { ViewAsBanner } from "@/features/view-as/view-as-banner";
import { cn } from "@/lib/utils";
import { useSession } from "@/hooks/use-session";

/**
 * Whether the desktop sidebar was left expanded or collapsed to icons.
 *
 * Kept in localStorage rather than the cookie the shadcn component writes,
 * because nothing on the server renders this page and would read it. Wrapped in
 * try/catch like every storage access: private windows and blocked site data
 * throw, and a sidebar that forgets is better than a page that fails to load.
 */
const SIDEBAR_STORAGE_KEY = "scandi-sidebar-open";

function readSidebarOpen(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
}

/** `/pipeline/b/:ref/presentacion` and every page under it: the script, a line, a shot. */
const PRESENTATION_PATH = /^\/pipeline\/b\/[^/]+\/presentacion(?:\/|$)/;
/** `/pipeline/b/:ref/deck`. */
const DECK_PATH = /^\/pipeline\/b\/[^/]+\/deck\/?$/;
/** The script chat, `/estudio/guion`, and one chat under it. */
// The Master AI's chats, and one artifact in its viewer: full-height screens like the studio's chat.
const MASTER_PATH = /^\/master(?:\/[^/]+)?\/?$/;
const ARTIFACT_VIEWER_PATH = /^\/artefactos\/\d+\/?$/;
const STUDIO_CHAT_PATH = /^\/estudio\/guion(?:\/[^/]+)?\/?$/;
/**
 * One ad job, `/estudio/trabajos/:jobId`, and its deck as the final presentation (`…/presentacion`): both
 * fill the content area. The job form (`/estudio/trabajos/nuevo`) is a page like any other.
 */
const STUDIO_JOB_PATH = /^\/estudio\/trabajos\/(?!nuevo\/?$)[^/]+(?:\/presentacion)?\/?$/;

/** The page's own content, the skip link's target. */
const MAIN_CONTENT_ID = "main-content";

export function AppLayout() {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { canUsePipeline } = useSession();
  const [open, setOpenState] = useState(readSidebarOpen);

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
    } catch {
      // Remembering is a convenience; see readSidebarOpen.
    }
  }, []);

  // The session browser puts a list beside a transcript, which needs more room
  // than a column of prose. It widens the same way the leaderboard does once
  // its own side column appears.
  // The pipeline lays cards out three across on a wide screen, and a brief puts
  // a rail beside the script; both need more than the column of prose the rest
  // of the app is set in.
  // A concept's deck is slides: it takes the whole content area, edge to edge
  // and top to bottom, like the coach (see `fills`), and draws its own frame.
  const deck = DECK_PATH.test(pathname);

  // The studio's chat puts its own list of chats beside the transcript, and a
  // job puts its deck preview beside its activity and the revision chat: both
  // are app screens rather than pages of prose, laid out edge to edge like the
  // coach (see `fills`).
  const studioChat = STUDIO_CHAT_PATH.test(pathname) || MASTER_PATH.test(pathname) || ARTIFACT_VIEWER_PATH.test(pathname);
  const studioJob = STUDIO_JOB_PATH.test(pathname);

  // The deck and the studio's working screens are the ones that want the whole screen.
  const widest = deck || studioChat || studioJob;

  // The presentation editor puts a script beside its shots: a line's words,
  // then a strip of 9:16 frames, and on a line or shot page a side panel. It
  // wants the full 6xl as soon as there is room for it, not only from xl.
  const presentation = PRESENTATION_PATH.test(pathname);

  // The editor's portal is the one place that wants room before xl: three
  // concept cards across is its whole layout, and at max-w-3xl it is two.
  const wide = widest || pathname === "/admin/actividad" // the Activity page's two columns take the full width
    ? "max-w-none"
    : presentation || pathname === "/admin/gasto" || pathname.startsWith("/settings") || pathname === "/admin/traducciones" || pathname === "/admin/agentes" || pathname === "/estudio/trabajos" || pathname === "/artefactos" || pathname.startsWith("/biblioteca") || pathname.startsWith("/ideas") // AI spend's, the settings hub's and the agents' tables; the ad jobs list; the library's and the ideas' grids
      ? "lg:max-w-6xl"
      : pathname === "/leaderboard" // the standings and a prize column side by side, then the videos: it uses a big screen
        ? "lg:max-w-6xl xl:max-w-7xl 2xl:max-w-[88rem]"
      : (pathname === "/" ||
          pathname === "/recursos" ||
          pathname === "/estudio" ||
          pathname === "/estudio/trabajos/nuevo" ||
          pathname.startsWith("/coach/sessions") ||
          pathname.startsWith("/pipeline") ||
          // The video screens: a table of an editor's videos, the ranking's poster grid, a creative's two columns.
          pathname === "/videos" ||
          pathname.startsWith("/editors/") ||
          pathname.startsWith("/creatives/")) &&
        "lg:max-w-5xl xl:max-w-6xl";

  // Pages that scroll inside themselves rather than scrolling the window. The
  // chat has its own transcript scroller with the composer pinned under it, and
  // the session browser has two independent ones. Either sitting in a page that
  // also scrolls would give the reader two scrollbars to choose from, so the
  // whole shell is pinned to the viewport height and the page gets what is left
  // under the top bar.
  // The Activity page sets its two columns (work sessions, the log) side by side, each scrolling on its own.
  // The studio's chat and a job's page are chats too: transcript, composer and side panels each scroll on their own.
  const fills =
    pathname === "/coach" || pathname.startsWith("/coach/sessions") || deck || pathname === "/admin/actividad" || studioChat || studioJob;

  return (
    <PageCrumbsProvider>
      <SidebarProvider
        open={open}
        onOpenChange={setOpen}
        className={cn(fills && "h-svh overflow-hidden")}
      >
        {/* First stop for Tab on every page: past the sidebar and the top bar. Focused by
            hand rather than followed as a #hash, because the deck keeps its slide in the hash. */}
        <a
          href={`#${MAIN_CONTENT_ID}`}
          onClick={(event) => {
            event.preventDefault();
            const main = document.getElementById(MAIN_CONTENT_ID);
            main?.focus();
            main?.scrollIntoView({ block: "start" });
          }}
          className="bg-background text-foreground focus-visible:ring-ring/50 sr-only z-50 rounded-lg border px-3 py-2 text-sm font-medium shadow-md focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus-visible:ring-3"
        >
          {t("nav.skipToContent")}
        </a>
        <AppSidebar />
        <SidebarInset className={cn("min-w-0", fills && "min-h-0 overflow-hidden")}>
          {/* The view-as strip rides with the top bar rather than above the whole
              shell: the desktop sidebar is fixed to the viewport's full height,
              and a strip above it would be half hidden behind it. On a phone the
              sidebar is a drawer, so the strip is the very top of the screen. */}
          <div className="sticky top-0 z-30 shrink-0">
            <ViewAsBanner />
            <header className="bg-background/80 flex h-14 shrink-0 items-center gap-2 border-b px-3 backdrop-blur-md sm:px-4">
              <SidebarTrigger
                className="-ml-1"
                aria-label={t("nav.toggleSidebar")}
                title={t("nav.toggleSidebar")}
              />
              <Separator orientation="vertical" className="mr-1 data-vertical:h-4 data-vertical:self-center" />
              <div className="min-w-0 flex-1">
                <PageBreadcrumb />
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                {canUsePipeline && <NotificationsBell />}
                <LanguageToggle />
                <ThemeToggle />
              </div>
            </header>
          </div>

          <div
            id={MAIN_CONTENT_ID}
            // Focusable by the skip link only, never by Tab, so no focus ring of its own.
            tabIndex={-1}
            className={cn(
              "mx-auto w-full flex-1 outline-none",
              fills ? "flex min-h-0 flex-col" : "max-w-3xl px-4 pt-4 pb-16 sm:px-6 sm:pt-6",
              wide,
            )}
          >
            <Outlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </PageCrumbsProvider>
  );
}
